import { useState, useEffect, useCallback } from 'react';

import { API } from "../config";

// Configuration de la limitation de fréquence
const REVIEW_CONFIG = {
  // Délai minimum entre deux demandes d'avis (en jours)
  minDaysBetweenPrompts: 30,
  
  // Nombre de jours d'utilisation avant la première demande
  minDaysBeforeFirstPrompt: 7,
  
  // Nombre maximum de fois qu'on peut demander un avis (0 = illimité)
  maxPrompts: 3,
  
  // Clé localStorage pour le suivi côté client (backup)
  localStorageKey: 'loveconnect_review_prompt',
};

/**
 * Hook pour gérer la logique de demande d'avis
 * Gère les déclencheurs, la limitation de fréquence, et le tracking
 */
export function useReviewPrompt(user) {
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);
  const [triggerSource, setTriggerSource] = useState('general');
  const [reviewStatus, setReviewStatus] = useState(null);

  // Charger le statut de review depuis le serveur ou localStorage
  useEffect(() => {
    if (user?.user_id) {
      loadReviewStatus();
    }
  }, [user?.user_id]);

  const loadReviewStatus = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API}/users/review-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReviewStatus(data);
      }
    } catch (error) {
      console.error('Error loading review status:', error);
      // Fallback: charger depuis localStorage
      const localData = localStorage.getItem(REVIEW_CONFIG.localStorageKey);
      if (localData) {
        setReviewStatus(JSON.parse(localData));
      }
    }
  };

  /**
   * Vérifie si on peut afficher la demande d'avis
   */
  const canShowPrompt = useCallback(() => {
    if (!user) return false;
    
    // Si l'utilisateur a déjà donné un avis positif (4-5 étoiles), ne plus demander
    if (reviewStatus?.hasGivenPositiveReview) {
      return false;
    }
    
    // Vérifier le nombre maximum de demandes
    if (REVIEW_CONFIG.maxPrompts > 0 && (reviewStatus?.promptCount || 0) >= REVIEW_CONFIG.maxPrompts) {
      return false;
    }
    
    // Vérifier le délai minimum entre les demandes
    if (reviewStatus?.lastPromptDate) {
      const lastPrompt = new Date(reviewStatus.lastPromptDate);
      const daysSinceLastPrompt = (Date.now() - lastPrompt.getTime()) / (1000 * 60 * 60 * 24);
      
      if (daysSinceLastPrompt < REVIEW_CONFIG.minDaysBetweenPrompts) {
        return false;
      }
    }
    
    return true;
  }, [user, reviewStatus]);

  /**
   * Vérifie si l'utilisateur utilise l'app depuis assez longtemps
   */
  const hasUsedAppLongEnough = useCallback(() => {
    if (!user?.created_at) return false;
    
    const createdAt = new Date(user.created_at);
    const daysSinceRegistration = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24);
    
    return daysSinceRegistration >= REVIEW_CONFIG.minDaysBeforeFirstPrompt;
  }, [user]);

  /**
   * Déclenche la demande d'avis après un match
   */
  const triggerAfterMatch = useCallback(() => {
    if (canShowPrompt()) {
      setTriggerSource('match');
      setShowReviewPrompt(true);
    }
  }, [canShowPrompt]);

  /**
   * Déclenche la demande d'avis après un achat de crédits
   */
  const triggerAfterPurchase = useCallback(() => {
    if (canShowPrompt()) {
      setTriggerSource('credits');
      setShowReviewPrompt(true);
    }
  }, [canShowPrompt]);

  /**
   * Déclenche la demande d'avis basée sur la durée d'utilisation
   * À appeler au chargement de l'app ou d'une page principale
   */
  const triggerAfterUsageDuration = useCallback(() => {
    if (canShowPrompt() && hasUsedAppLongEnough()) {
      // Vérifier si c'est la première demande basée sur la durée
      if (!reviewStatus?.lastPromptDate) {
        setTriggerSource('usage');
        setShowReviewPrompt(true);
      }
    }
  }, [canShowPrompt, hasUsedAppLongEnough, reviewStatus]);

  /**
   * Ferme la modal et enregistre les données
   */
  const handleReviewResponse = useCallback(async (response) => {
    const { rating, action, feedback } = response;
    
    const newStatus = {
      lastPromptDate: new Date().toISOString(),
      promptCount: (reviewStatus?.promptCount || 0) + 1,
      lastRating: rating,
      lastAction: action,
      hasGivenPositiveReview: rating >= 4 && action === 'play_store',
    };
    
    // Sauvegarder localement
    localStorage.setItem(REVIEW_CONFIG.localStorageKey, JSON.stringify(newStatus));
    setReviewStatus(newStatus);
    
    // Sauvegarder sur le serveur
    try {
      const token = localStorage.getItem('session_token');
      await fetch(`${API}/users/review-response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          rating,
          action,
          feedback: feedback || null,
          trigger_source: triggerSource
        })
      });
    } catch (error) {
      console.error('Error saving review response:', error);
    }
    
    setShowReviewPrompt(false);
  }, [reviewStatus, triggerSource]);

  /**
   * Ferme la modal sans enregistrer (dismiss)
   */
  const closePrompt = useCallback(() => {
    setShowReviewPrompt(false);
  }, []);

  return {
    // État
    showReviewPrompt,
    triggerSource,
    reviewStatus,
    
    // Actions
    triggerAfterMatch,
    triggerAfterPurchase,
    triggerAfterUsageDuration,
    handleReviewResponse,
    closePrompt,
    
    // Helpers
    canShowPrompt,
    hasUsedAppLongEnough,
  };
}

// Export de la configuration pour référence
export { REVIEW_CONFIG };
